import { TOURNAMENT_GAME_RESULT, TOURNAMENT_GAME_RESULT_TEXT, } from "../../constants";
/**
 * Tournament result validation and auto-completion utilities
 * Moved from Vue client to shared library for reuse across different contexts
 */
/**
 * Valid result combinations for tournament games
 * Maintains simple list as preferred by user instead of complex conditional logic
 */
const VALID_RESULT_COMBINATIONS = [
    // Win/Loss combinations
    [TOURNAMENT_GAME_RESULT.WIN, TOURNAMENT_GAME_RESULT.LOSS],
    [TOURNAMENT_GAME_RESULT.LOSS, TOURNAMENT_GAME_RESULT.WIN],
    // Draw combinations
    [TOURNAMENT_GAME_RESULT.DRAW, TOURNAMENT_GAME_RESULT.DRAW],
    // Forfeit combinations
    [TOURNAMENT_GAME_RESULT.FORFEITW, TOURNAMENT_GAME_RESULT.FORFEITL],
    [TOURNAMENT_GAME_RESULT.FORFEITL, TOURNAMENT_GAME_RESULT.FORFEITW],
    [TOURNAMENT_GAME_RESULT.FORFEITL, TOURNAMENT_GAME_RESULT.FORFEITL],
    [TOURNAMENT_GAME_RESULT.FORFEITD, TOURNAMENT_GAME_RESULT.FORFEITD],
    // Unfinished combinations
    [TOURNAMENT_GAME_RESULT.UNFINISHED, TOURNAMENT_GAME_RESULT.UNFINISHED],
    // Bye combinations (single player scenarios)
    [TOURNAMENT_GAME_RESULT.FULLBYE, null],
    [null, TOURNAMENT_GAME_RESULT.FULLBYE],
    [TOURNAMENT_GAME_RESULT.ZEROBYE, null],
    [null, TOURNAMENT_GAME_RESULT.ZEROBYE],
    [TOURNAMENT_GAME_RESULT.HALFBYE, null],
    [null, TOURNAMENT_GAME_RESULT.HALFBYE],
    [TOURNAMENT_GAME_RESULT.PAIRINGBYE, null],
    [null, TOURNAMENT_GAME_RESULT.PAIRINGBYE],
    // Disqualified combinations
    [TOURNAMENT_GAME_RESULT.DISQUALIFIED, TOURNAMENT_GAME_RESULT.WIN],
    [TOURNAMENT_GAME_RESULT.WIN, TOURNAMENT_GAME_RESULT.DISQUALIFIED],
    [TOURNAMENT_GAME_RESULT.DISQUALIFIED, TOURNAMENT_GAME_RESULT.DISQUALIFIED],
];
/**
 * Get the opponent result for auto-completion
 * @param result - The result to get opponent for
 * @returns The opponent result
 */
export const getOpponentResult = (result) => {
    const oppositeResults = {
        [TOURNAMENT_GAME_RESULT.WIN]: TOURNAMENT_GAME_RESULT.LOSS,
        [TOURNAMENT_GAME_RESULT.LOSS]: TOURNAMENT_GAME_RESULT.WIN,
        [TOURNAMENT_GAME_RESULT.DRAW]: TOURNAMENT_GAME_RESULT.DRAW,
        [TOURNAMENT_GAME_RESULT.FORFEITW]: TOURNAMENT_GAME_RESULT.FORFEITL,
        [TOURNAMENT_GAME_RESULT.FORFEITL]: TOURNAMENT_GAME_RESULT.FORFEITW,
        [TOURNAMENT_GAME_RESULT.FORFEITD]: TOURNAMENT_GAME_RESULT.FORFEITD,
        [TOURNAMENT_GAME_RESULT.UNFINISHED]: TOURNAMENT_GAME_RESULT.UNFINISHED,
        [TOURNAMENT_GAME_RESULT.DISQUALIFIED]: TOURNAMENT_GAME_RESULT.WIN,
    };
    return oppositeResults[result] ?? null;
};
/**
 * Check if a result combination is valid
 * @param whiteResult - White player result
 * @param blackResult - Black player result
 * @returns Whether the combination is valid
 */
export const isValidResultCombination = (whiteResult, blackResult) => {
    return VALID_RESULT_COMBINATIONS.some(([white, black]) => white === whiteResult && black === blackResult);
};
/**
 * Validate game results and return validation object
 * @param whiteResult - White player result
 * @param blackResult - Black player result
 * @returns Validation result with isValid and message
 */
export const validateGameResults = (whiteResult, blackResult) => {
    // Allow null results (unset) - both must be null for it to be valid
    if (whiteResult == null && blackResult == null) {
        return { isValid: true };
    }
    // Check if the combination is in our valid list
    const isValid = isValidResultCombination(whiteResult, blackResult);
    if (!isValid) {
        const whiteText = whiteResult !== null
            ? TOURNAMENT_GAME_RESULT_TEXT[whiteResult] || "UNKNOWN"
            : "UNSET";
        const blackText = blackResult !== null
            ? TOURNAMENT_GAME_RESULT_TEXT[blackResult] || "UNKNOWN"
            : "UNSET";
        return {
            isValid: false,
            message: `Invalid result combination: ${whiteText} vs ${blackText}`,
        };
    }
    return { isValid: true };
};
/**
 * Smart auto-completion for tournament results
 * Validates existing results before auto-setting as preferred by user
 * @param game - The tournament game object
 * @param whiteResult - New white result (null if unchanged)
 * @param blackResult - New black result (null if unchanged)
 * @returns Object with autoWhiteResult and autoBlackResult
 */
export const getSmartAutoCompletedResults = (game, whiteResult, blackResult) => {
    let autoWhiteResult, autoBlackResult;
    if (whiteResult !== null && blackResult === null) {
        // White result was just set, use it and determine black result
        autoWhiteResult = whiteResult;
        // Check if we need to auto-complete black
        if (game.blackResult === null ||
            game.blackResult === undefined ||
            !isValidResultCombination(whiteResult, game.blackResult)) {
            autoBlackResult = getOpponentResult(whiteResult);
        }
        else {
            // Keep existing black result if it's valid with the new white result
            autoBlackResult = game.blackResult;
        }
    }
    else if (blackResult !== null && whiteResult === null) {
        // Black result was just set, use it and determine white result
        autoBlackResult = blackResult;
        // Check if we need to auto-complete white
        if (game.whiteResult === null ||
            game.whiteResult === undefined ||
            !isValidResultCombination(game.whiteResult, blackResult)) {
            autoWhiteResult = getOpponentResult(blackResult);
        }
        else {
            // Keep existing white result if it's valid with the new black result
            autoWhiteResult = game.whiteResult;
        }
    }
    else {
        // Fallback: use provided values or existing game values
        autoWhiteResult = whiteResult !== null ? whiteResult : game.whiteResult;
        autoBlackResult = blackResult !== null ? blackResult : game.blackResult;
    }
    return { autoWhiteResult, autoBlackResult };
};
/**
 * Check if game has invalid results
 * @param game - The tournament game object
 * @returns Whether the game has invalid results
 */
export const hasInvalidResults = (game) => {
    const validation = validateGameResults(game.whiteResult, game.blackResult);
    return !validation.isValid;
};
/**
 * Get invalid result message for a game
 * @param game - The tournament game object
 * @returns The invalid result message
 */
export const getInvalidResultMessage = (game) => {
    const validation = validateGameResults(game.whiteResult, game.blackResult);
    return validation.message || "";
};
